import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type {
  AnalyticsEventContract,
  DeviceAnalyticsContract,
  VisitorRecordContract,
} from '@/contracts/analytics'
import type {
  FormManagementVisitorSession,
  FormManagementWorkspaceData,
} from '@/contracts/form-management'
import {
  type AnalyticsVisitorActivityClient,
  analyticsVisitorActivityClient as defaultAnalyticsVisitorActivityClient,
} from '@/lib/data-client/analytics'
import { isApiDataClientMode } from '@/lib/data-client/mode'
import {
  AlertTriangle,
  Loader2,
  type LucideIcon,
  MonitorSmartphone,
  RefreshCw,
  ShieldCheck,
  Timer,
  Users,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useState } from 'react'

type FormManagementVisitorActivityProps = {
  workspaceData: FormManagementWorkspaceData
  analyticsClient?: AnalyticsVisitorActivityClient
}

type D1VisitorActivityState = {
  devices: DeviceAnalyticsContract[]
  visitors: VisitorRecordContract[]
  events: AnalyticsEventContract[]
  loading: boolean
  error: string | null
  loadedAt: string | null
}

const initialD1VisitorActivityState: D1VisitorActivityState = {
  devices: [],
  visitors: [],
  events: [],
  loading: false,
  error: null,
  loadedAt: null,
}

const statusLabels: Record<FormManagementVisitorSession['status'], string> = {
  completed: '已完成',
  started: '進行中',
  abandoned: '已離開',
}

const statusVariants: Record<
  FormManagementVisitorSession['status'],
  'success' | 'warning' | 'secondary'
> = {
  completed: 'success',
  started: 'warning',
  abandoned: 'secondary',
}

const analyticsEventLabels: Record<AnalyticsEventContract['eventType'], string> = {
  form_viewed: '開啟問卷',
  form_started: '開始填寫',
  question_viewed: '查看題目',
  question_answered: '回答題目',
  form_submitted: '送出問卷',
  form_abandoned: '離開問卷',
}

export function FormManagementVisitorActivity({
  workspaceData,
  analyticsClient = defaultAnalyticsVisitorActivityClient,
}: FormManagementVisitorActivityProps) {
  const isApiMode = isApiDataClientMode()
  const formId = workspaceData.snapshotPreview.formId
  const [d1State, setD1State] = useState<D1VisitorActivityState>(initialD1VisitorActivityState)

  const loadD1VisitorActivity = useCallback(async () => {
    setD1State((current) => ({ ...current, loading: true, error: null }))
    try {
      const [devicesResult, visitorsResult, eventsResult] = await Promise.all([
        analyticsClient.getAnalyticsDevices(formId),
        analyticsClient.getAnalyticsVisitors(formId),
        analyticsClient.getAnalyticsEvents(formId),
      ])
      setD1State({
        devices: devicesResult.data,
        visitors: visitorsResult.data,
        events: eventsResult.data,
        loading: false,
        error: null,
        loadedAt: new Date().toISOString(),
      })
    } catch (error) {
      setD1State((current) => ({
        ...current,
        loading: false,
        error: getSafeAnalyticsError(error),
      }))
    }
  }, [analyticsClient, formId])

  useEffect(() => {
    if (!isApiMode) return
    void loadD1VisitorActivity()
  }, [isApiMode, loadD1VisitorActivity])

  if (isApiMode) {
    return <D1VisitorActivityPanel state={d1State} onRefresh={loadD1VisitorActivity} />
  }

  return <MockSafeVisitorActivityPanel workspaceData={workspaceData} />
}

function D1VisitorActivityPanel({
  state,
  onRefresh,
}: {
  state: D1VisitorActivityState
  onRefresh: () => void
}) {
  const completed = state.visitors.filter((visitor) => visitor.completed).length
  const started = state.visitors.filter((visitor) => visitor.started && !visitor.completed).length
  const abandoned = state.events.filter((event) => event.eventType === 'form_abandoned').length
  const isEmpty =
    state.devices.length === 0 && state.visitors.length === 0 && state.events.length === 0
  const submittedEvents = state.events.filter(
    (event) => event.eventType === 'form_submitted',
  ).length

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="success">D1</Badge>
            <Badge variant="outline">authenticated API</Badge>
            <Badge variant="outline">safe analytics</Badge>
          </div>
          <h3 className="mt-3 font-semibold text-slate-950">D1 訪客活動</h3>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
            顯示後端 D1 analytics devices、visitors、events 的安全欄位；不顯示 session id、raw
            metadata、IP、user agent 或填答明細。
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          className="gap-2 self-start"
          disabled={state.loading}
          onClick={onRefresh}
        >
          {state.loading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <RefreshCw className="size-4" />
          )}
          重新整理 D1 訪客活動
        </Button>
      </div>

      {state.loading ? (
        <output
          aria-live="polite"
          className="flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-800"
        >
          <Loader2 className="size-4 animate-spin" />
          D1 analytics 資料載入中
        </output>
      ) : null}

      {state.error ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div>
            <div className="font-medium">D1 訪客活動暫時不可用</div>
            <div className="mt-1">{state.error}</div>
          </div>
        </div>
      ) : null}

      {!state.loading && !state.error && isEmpty ? (
        <output className="block rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center">
          <div className="font-medium text-slate-800">目前沒有 D1 訪客活動資料</div>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            後端 D1 route 已可讀取，但 visitor/session/event writer 尚未產生資料時會顯示空狀態。
          </p>
        </output>
      ) : null}

      <div className="grid gap-3 md:grid-cols-4">
        <MetricTile icon={Users} label="訪客紀錄" value={state.visitors.length} />
        <MetricTile icon={Timer} label="完成 / 進行中" value={`${completed} / ${started}`} />
        <MetricTile icon={MonitorSmartphone} label="裝置分佈" value={state.devices.length} />
        <MetricTile
          icon={ShieldCheck}
          label="送出 / 離開事件"
          value={`${submittedEvents} / ${abandoned}`}
        />
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
        <SafeTableSection
          title="裝置統計"
          badge={`${state.devices.length} 類`}
          tableLabel="D1 analytics devices table"
        >
          <TableHeader>
            <TableRow>
              <TableHead>裝置</TableHead>
              <TableHead>訪客</TableHead>
              <TableHead>完成率</TableHead>
              <TableHead>平均時間</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {state.devices.length > 0 ? (
              state.devices.map((device) => (
                <TableRow key={device.device}>
                  <TableCell className="font-medium text-slate-900">{device.label}</TableCell>
                  <TableCell>{device.visitors}</TableCell>
                  <TableCell>{device.completionRate}%</TableCell>
                  <TableCell>{formatSeconds(device.averageTimeSeconds)}</TableCell>
                </TableRow>
              ))
            ) : (
              <EmptyTableRow colSpan={4} label="尚無裝置統計" />
            )}
          </TableBody>
        </SafeTableSection>

        <SafeTableSection
          title="事件紀錄"
          badge={`${state.events.length} 筆`}
          tableLabel="D1 analytics events table"
        >
          <TableHeader>
            <TableRow>
              <TableHead>事件</TableHead>
              <TableHead>發生時間</TableHead>
              <TableHead>題目</TableHead>
              <TableHead>連結狀態</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {state.events.length > 0 ? (
              state.events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell>
                    <div className="font-medium text-slate-900">
                      {analyticsEventLabels[event.eventType]}
                    </div>
                    <div className="text-xs text-slate-500">{event.eventType}</div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDateTime(event.occurredAt)}
                  </TableCell>
                  <TableCell>{event.questionId ?? '未綁定題目'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      <Badge variant={event.visitorLinked ? 'success' : 'secondary'}>visitor</Badge>
                      <Badge variant={event.responseLinked ? 'success' : 'secondary'}>
                        response
                      </Badge>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <EmptyTableRow colSpan={4} label="尚無事件紀錄" />
            )}
          </TableBody>
        </SafeTableSection>
      </div>

      <SafeTableSection
        title="訪客紀錄"
        badge={`${state.visitors.length} 筆`}
        tableLabel="D1 analytics visitors table"
      >
        <TableHeader>
          <TableRow>
            <TableHead>來源 / UTM</TableHead>
            <TableHead>裝置 / 瀏覽器</TableHead>
            <TableHead>地區</TableHead>
            <TableHead>開始 / 完成</TableHead>
            <TableHead>Hidden fields</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {state.visitors.length > 0 ? (
            state.visitors.map((visitor) => (
              <TableRow key={visitor.id}>
                <TableCell className="min-w-56">
                  <div className="font-medium text-slate-900">{visitor.sourceLabel}</div>
                  <div className="mt-1 text-xs text-slate-500">
                    {visitor.utm.source} / {visitor.utm.medium} / {visitor.utm.campaign}
                  </div>
                </TableCell>
                <TableCell className="min-w-44">
                  <div>{visitor.deviceLabel}</div>
                  <div className="text-xs text-slate-500">
                    {visitor.browser || '未標示瀏覽器'}，
                    {formatSeconds(visitor.sessionDurationSeconds)}
                  </div>
                </TableCell>
                <TableCell>{formatLocation(visitor)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    <Badge variant={visitor.started ? 'success' : 'secondary'}>開始</Badge>
                    <Badge variant={visitor.completed ? 'success' : 'warning'}>完成</Badge>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {formatDateTime(visitor.visitedAt)}
                  </div>
                </TableCell>
                <TableCell>
                  <HiddenFieldBadges hiddenFields={visitor.hiddenFields} />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <EmptyTableRow colSpan={5} label="尚無訪客紀錄" />
          )}
        </TableBody>
      </SafeTableSection>

      {state.loadedAt ? (
        <div className="text-xs text-slate-500">最後更新：{formatDateTime(state.loadedAt)}</div>
      ) : null}
    </div>
  )
}

function MockSafeVisitorActivityPanel({
  workspaceData,
}: { workspaceData: FormManagementWorkspaceData }) {
  const completed = workspaceData.visitorSessions.filter(
    (session) => session.status === 'completed',
  ).length
  const active = workspaceData.visitorSessions.filter(
    (session) => session.status === 'started',
  ).length
  const abandoned = workspaceData.visitorSessions.filter(
    (session) => session.status === 'abandoned',
  ).length

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge>Mock-safe</Badge>
        <Badge variant="secondary">contract preview</Badge>
        <Badge variant="outline">session-level analytics</Badge>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <MetricTile icon={Users} label="Session 數" value={workspaceData.visitorSessions.length} />
        <MetricTile icon={Timer} label="完成 / 進行中" value={`${completed} / ${active}`} />
        <MetricTile icon={MonitorSmartphone} label="離開 session" value={abandoned} />
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 size-5 text-amber-500" />
            <div>
              <h3 className="font-semibold text-slate-950">訪客紀錄狀態</h3>
              <p className="mt-1 text-sm leading-6 text-slate-500">
                此頁目前顯示 session-level analytics preview。正式 API mode 會讀取 D1
                devices、visitors、events safe fields。
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <SafeTableSection
        title="Session table"
        badge={workspaceData.panelStates.visitorActivity}
        tableLabel="mock-safe visitor session preview table"
      >
        <TableHeader>
          <TableRow>
            <TableHead>來源</TableHead>
            <TableHead>UTM / Campaign</TableHead>
            <TableHead>Device</TableHead>
            <TableHead>Started</TableHead>
            <TableHead>Completed</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Hidden fields</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {workspaceData.visitorSessions.map((session) => (
            <TableRow key={session.id}>
              <TableCell className="font-medium text-slate-900">{session.sourceLabel}</TableCell>
              <TableCell>
                <div>{session.utmSource}</div>
                <div className="text-xs text-slate-500">{session.campaign}</div>
              </TableCell>
              <TableCell>
                <div>{session.device}</div>
                <div className="text-xs text-slate-500">{session.browser}</div>
              </TableCell>
              <TableCell>{session.startedAt}</TableCell>
              <TableCell>{session.completedAt ?? '-'}</TableCell>
              <TableCell>
                <Badge variant={statusVariants[session.status]}>
                  {statusLabels[session.status]}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex min-w-52 flex-wrap gap-1">
                  {session.hiddenFields.map((field) => (
                    <span
                      key={`${session.id}-${field.label}`}
                      className="rounded bg-slate-100 px-2 py-1 text-xs"
                    >
                      {field.label}: {field.value}
                    </span>
                  ))}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </SafeTableSection>
    </div>
  )
}

function MetricTile({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon
  label: string
  value: number | string
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">{label}</span>
          <Icon className="size-4 text-indigo-600" />
        </div>
        <div className="mt-3 text-2xl font-semibold text-slate-950">{value}</div>
      </CardContent>
    </Card>
  )
}

function SafeTableSection({
  title,
  badge,
  tableLabel,
  children,
}: {
  title: string
  badge: string
  tableLabel: string
  children: ReactNode
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="font-semibold text-slate-950">{title}</h4>
          <Badge variant="outline">{badge}</Badge>
        </div>
        <div className="mt-4 overflow-x-auto rounded-md border border-slate-200">
          <Table aria-label={tableLabel} className="min-w-[760px]">
            {children}
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

function EmptyTableRow({ colSpan, label }: { colSpan: number; label: string }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="h-24 text-center text-sm text-slate-500">
        {label}
      </TableCell>
    </TableRow>
  )
}

function HiddenFieldBadges({ hiddenFields }: { hiddenFields: Record<string, string> }) {
  const entries = Object.entries(hiddenFields)
  if (entries.length === 0) return <span className="text-sm text-slate-500">無</span>

  return (
    <div className="flex min-w-56 flex-wrap gap-1">
      {entries.map(([key, value]) => (
        <Badge key={key} variant="outline">
          {key}: {value}
        </Badge>
      ))}
    </div>
  )
}

function formatSeconds(seconds: number) {
  const minutes = Math.floor(seconds / 60)
  const remaining = seconds % 60
  return `${minutes}m ${remaining}s`
}

function formatDateTime(value: string) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toISOString().replace('T', ' ').slice(0, 19)
}

function formatLocation(visitor: VisitorRecordContract) {
  const parts = [visitor.city, visitor.country].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : '未標示'
}

function getSafeAnalyticsError(error: unknown) {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = String((error as { code?: unknown }).code ?? 'unknown_error')
    return `API 暫時無法讀取 D1 analytics，安全代碼 ${code}。`
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return 'API 暫時無法讀取 D1 analytics，請稍後再試。'
}
